import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, InputNumber, DatePicker, Select, message } from 'antd';
import type { UserStatus } from '@/types/models';
import type { Participant } from '@/types/entities';
import UserService from '@/services/user.service';
import CategoryService from '@/services/category.service';
import { logger } from '@/utils/logger';
import { apiService } from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { getDateValueFromEvent, datePickerProps } from '@/utils/datePickerUtils';

dayjs.locale('ru');


interface EditParticipantModalProps {
  visible: boolean;
  participant: Participant | null;
  clubId?: number; // Добавляем параметр clubId для фильтрации тренеров
  onCancel: () => void;
  onSuccess: () => void;
}

interface ParticipantFormValues {
  firstName: string;
  lastName: string;
  middleName?: string;
  dateOfBirth: dayjs.Dayjs | null | undefined;
  gender: 'male' | 'female';
  weight: number;
  beltLevel: string;
  kyu?: string; // Add kyu field for form handling
  categoryId?: number;
  coachId?: number;
  status: UserStatus;
}

export const EditParticipantModal: React.FC<EditParticipantModalProps> = ({
  visible,
  participant,
  clubId,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm<ParticipantFormValues>();
  const [coaches, setCoaches] = useState<any[]>([]);
  const [allCategories, setAllCategories] = useState<any[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [currentParticipant, setCurrentParticipant] = useState<Participant | null>(null);

  // Функция для фильтрации категорий по возрасту, весу и полу
  const filterCategories = (birthDate: dayjs.Dayjs | null, weight: number | null, gender: string | null) => {
    if (!birthDate || !weight || !gender) {
      setFilteredCategories(allCategories);
      return;
    }

    // Рассчитываем возраст
    const today = dayjs();
    let calculatedAge = today.year() - birthDate.year();
    const monthDiff = today.month() - birthDate.month();
    if (monthDiff < 0 || (monthDiff === 0 && today.date() < birthDate.date())) {
      calculatedAge--;
    }

    // Фильтруем категории
    const filtered = allCategories.filter(category => {
      // Проверка возраста
      const ageMatch = calculatedAge >= category.ageFrom && calculatedAge <= category.ageTo;
      
      // Проверка веса
      const weightMatch = weight >= category.weightFrom && weight <= category.weightTo;
      
      // Проверка пола
      const genderMatch = category.gender === gender || category.gender === 'mixed';
      
      return ageMatch && weightMatch && genderMatch;
    });

    setFilteredCategories(filtered);
  };


  // Загрузка списка тренеров
  const fetchCoaches = async () => {
    try {
      setLoading(true);

      // Если передан clubId, загружаем только тренеров этого клуба
      if (clubId) {
        const response = await UserService.getClubCoaches(clubId);
        if (response.data && Array.isArray(response.data)) {
          // Добавляем полное имя для отображения
          const coachesWithFullName = response.data.map(coach => ({
            ...coach,
            fullName: `${coach.lastName || ''} ${coach.firstName || ''}${coach.user?.middleName ? ` ${coach.user.middleName}` : ''}`
          }));
          setCoaches(coachesWithFullName);          
        }
      } else {
        // Если clubId не передан, загружаем всех тренеров (старое поведение)
        const { data } = await apiService.get<{ data: any[], status: number }>(API_ENDPOINTS.COACHES.BASE);
        if (data?.data) {
          setCoaches(data.data);
        }
      }
    } catch (error) {
      logger.error('Error fetching coaches:', error);
      message.error('Ошибка при загрузке списка тренеров');
    } finally {
      setLoading(false);
    }
  };

  // Загрузка списка категорий
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const categoriesData = await CategoryService.getAll();
      setAllCategories(categoriesData);
      setFilteredCategories(categoriesData);
    } catch (error) {
      logger.error('Error fetching categories:', error);
      message.error('Ошибка при загрузке списка категорий');
    } finally {
      setLoadingCategories(false);
    }
  };

  // Загрузка актуальных данных участника из БД при открытии модального окна
  const fetchParticipantData = async () => {
    if (!participant?.id) return;

    try {      
      // Загружаем актуальные данные участника из БД
      const response: any = await apiService.get(`${API_ENDPOINTS.PARTICIPANTS.BASE}/${participant.id}`);
      const participantData = response.data?.data || response.data;
      
      if (participantData) {
        setCurrentParticipant(participantData);
      } else {
        logger.warn('Не удалось загрузить данные участника, используем переданные данные');
        setCurrentParticipant(participant);
      }
    } catch (error) {
      logger.error('Ошибка при загрузке данных участника:', error);
      // В случае ошибки используем переданные данные
      setCurrentParticipant(participant);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchCoaches();
      fetchCategories();
      fetchParticipantData(); // Загружаем актуальные данные из БД
    } else {
      // Сбрасываем данные при закрытии модального окна
      setCurrentParticipant(null);
      form.resetFields();
    }
  }, [visible, participant?.id]); // Зависимость от ID участника, а не от всего объекта

  // Обновляем форму когда загружены актуальные данные
  useEffect(() => {
    if (currentParticipant && visible) {
      // Парсим дату рождения с проверкой валидности
      // Проверяем оба поля: dateOfBirth и birthDate
      let parsedDateOfBirth: dayjs.Dayjs | undefined = undefined;
      const dateValue = (currentParticipant as any).dateOfBirth || (currentParticipant as any).birthDate;
      
      if (dateValue) {
        // Пробуем разные форматы даты
        if (typeof dateValue === 'string') {
          // Если это ISO строка или другой формат
          let parsed = dayjs(dateValue);
          if (!parsed.isValid()) {
            // Пробуем парсить как дату в формате YYYY-MM-DD
            parsed = dayjs(dateValue, 'YYYY-MM-DD', true);
          }
          
          if (parsed.isValid()) {
            parsedDateOfBirth = parsed;            
          } else {
            logger.warn('Could not parse dateOfBirth:', dateValue);
          }
        } else if (dateValue instanceof Date) {
          parsedDateOfBirth = dayjs(dateValue);
          if (parsedDateOfBirth.isValid()) {
            
          } else {
            logger.warn('Invalid Date object:', dateValue);
          }
        }
      } else {
        logger.warn('No dateOfBirth or birthDate found in participant data');
      }

      // Используем setTimeout для обеспечения того, что форма полностью инициализирована
      setTimeout(() => {
        const formValues: any = {
          firstName: currentParticipant.firstName,
          lastName: currentParticipant.lastName,
          middleName: currentParticipant.middleName,
          gender: currentParticipant.gender,
          weight: currentParticipant.weight,
          kyu: currentParticipant.beltLevel, // Исправляем несоответствие имен полей - в форме kyu, в данных beltLevel
          categoryId: currentParticipant.categoryId,
          coachId: currentParticipant.coachId,
          status: currentParticipant.status
        };
        
        // Устанавливаем дату рождения только если она успешно распарсена
        if (parsedDateOfBirth && parsedDateOfBirth.isValid()) {
          formValues.dateOfBirth = parsedDateOfBirth;
        }
        
        form.setFieldsValue(formValues);
        // Фильтруем категории после установки значений формы
        setTimeout(() => {
          const gender = formValues.gender;
          const weight = formValues.weight;
          const dateOfBirth = parsedDateOfBirth;
          if (gender && weight && dateOfBirth) {
            filterCategories(dateOfBirth, weight, gender);
          }
        }, 150);
      }, 100); // Увеличиваем задержку для более надежной инициализации
    }
  }, [currentParticipant, visible, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!participant) return;

      // Создаем объект с данными формы
      const formValues = { ...values };

      // Удаляем поле kyu, чтобы оно не отправлялось на сервер
      const { kyu, ...restValues } = formValues;

      // Проверяем, что поле kyu не пустое
      if (!kyu) {
        message.error('Уровень пояса (кю) обязателен');
        return;
      }

      // Создаем объект для отправки на сервер
      const updateData: Partial<Participant> = {
        ...restValues,
        dateOfBirth: formValues.dateOfBirth?.format('YYYY-MM-DD') as string,
        gender: formValues.gender,
        weight: formValues.weight || undefined,
        status: formValues.status as UserStatus,
        beltLevel: kyu, // Добавляем преобразование поля kyu в beltLevel для API
      };

      // Удаляем null и undefined значения, чтобы не отправлять их на сервер
      Object.keys(updateData).forEach(key => {
        const value = (updateData as any)[key];
        if (value === null || value === undefined) {
          delete (updateData as any)[key];
        }
      });

      // Используем актуальные данные участника или переданные данные
      const participantToUpdate = currentParticipant || participant;
      if (!participantToUpdate) return;

      // Добавляем clubId, если он есть у участника (обязателен для валидации на бэкенде)
      // Валидатор требует clubId как обязательное поле
      if (participantToUpdate.clubId !== undefined && participantToUpdate.clubId !== null && participantToUpdate.clubId > 0) {
        updateData.clubId = participantToUpdate.clubId;
      } else if (clubId !== undefined && clubId !== null && clubId > 0) {
        // Если clubId передан как проп, используем его
        updateData.clubId = clubId;
      } else {
        // Если clubId не найден, логируем предупреждение и пытаемся получить из участника
        logger.warn(`clubId не найден для участника ${participantToUpdate.id}, проверяем альтернативные источники`);
        // Проверяем, есть ли clubId в других полях участника
        const alternativeClubId = (participantToUpdate as any).club?.id || (participantToUpdate as any).clubId;
        if (alternativeClubId && alternativeClubId > 0) {
          updateData.clubId = alternativeClubId;
        } else {
          logger.error(`clubId не найден для участника ${participantToUpdate.id}, это может вызвать ошибку валидации`);
        }
      }
      
      const response = await UserService.updateParticipant(participantToUpdate.id, updateData);
      
      // Обновляем локальные данные участника из ответа сервера
      if (response?.data) {
        setCurrentParticipant(response.data);
      }
      
      message.success('Данные спортсмена успешно обновлены');
      
      // Небольшая задержка перед вызовом onSuccess, чтобы дать время БД обновиться
      setTimeout(() => {
        onSuccess();
      }, 100);
    } catch (error: any) {
      logger.error('Error updating participant:', error);

      // Улучшаем обработку ошибок для более информативных сообщений
      if (error.errorFields) {
        // Ошибка валидации формы
        const fieldErrors = error.errorFields.map((field: any) => `${field.errors.join(', ')}`);
        message.error(`Ошибка валидации: ${fieldErrors.join('; ')}`);
      } else if (error.response?.status === 404) {
        // Ошибка 404 - ресурс не найден
        const participantToUpdate = currentParticipant || participant;
        message.error(`Спортсмен с ID ${participantToUpdate?.id} не найден или недоступен`);
      } else if (error.response?.status === 400) {
        // Ошибка 400 - неверный запрос
        let errorMessage = 'Ошибка валидации данных';

        // Пытаемся получить детали ошибки
        if (error.response?.data?.message) {
          errorMessage = `Ошибка: ${error.response.data.message}`;
        } else if (error.response?.data?.error) {
          errorMessage = `Ошибка: ${error.response.data.error}`;
        } else if (typeof error.response?.data === 'string') {
          errorMessage = `Ошибка: ${error.response.data}`;
        }        

        message.error(errorMessage);
      } else if (error.response?.data?.message) {
        // Сообщение об ошибке от сервера
        message.error(`Ошибка: ${error.response.data.message}`);
      } else {
        // Общая ошибка
        message.error('Ошибка при обновлении данных спортсмена');
      }
    }
  };

  return (
    <Modal
      title="Редактирование спортсмена"
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText="Сохранить"
      cancelText="Отмена"
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          name="firstName"
          label="Имя"
          rules={[{ required: true, message: 'Введите имя' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="lastName"
          label="Фамилия"
          rules={[{ required: true, message: 'Введите фамилию' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="middleName"
          label="Отчество"
        >
          <Input />
        </Form.Item>

        {/* Добавляем поле выбора пола */}
        <Form.Item
          name="gender"
          label="Пол"
          rules={[{ required: true, message: 'Выберите пол' }]}
        >
          <Select
            onChange={(value) => {
              const dateOfBirth = form.getFieldValue('dateOfBirth');
              const weight = form.getFieldValue('weight');
              filterCategories(dateOfBirth, weight, value);
            }}
          >
            <Select.Option value="male">Мужской</Select.Option>
            <Select.Option value="female">Женский</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="dateOfBirth"
          label="Дата рождения"
          rules={[{ required: true, message: 'Выберите дату рождения' }]}
          getValueFromEvent={getDateValueFromEvent}
        >
          <DatePicker 
            {...datePickerProps}
            placeholder="Выберите дату рождения (ДД.ММ.ГГГГ)"
            onChange={(date) => {
              const gender = form.getFieldValue('gender');
              const weight = form.getFieldValue('weight');
              filterCategories(date, weight, gender);
            }}
          />
        </Form.Item>

        <Form.Item
          name="weight"
          label="Вес (кг)"
          rules={[
            { required: true, message: 'Введите вес' },
            { type: 'number', min: 0.1, message: 'Вес должен быть положительным числом' }
          ]}
        >
          <InputNumber 
            min={0.1} 
            max={200} 
            step={0.1} 
            precision={1} 
            style={{ width: '100%' }}
            onChange={(value) => {
              const dateOfBirth = form.getFieldValue('dateOfBirth');
              const gender = form.getFieldValue('gender');
              filterCategories(dateOfBirth, value as number, gender);
            }}
          />
        </Form.Item>

        <Form.Item
          name="kyu"
          label="Уровень"
          rules={[{ required: true, message: 'Выберите уровень' }]}
        >
          <Select>
            <Select.Option value="11_kyu">11 кю</Select.Option>
            <Select.Option value="10_kyu">10 кю</Select.Option>
            <Select.Option value="9_kyu">9 кю</Select.Option>
            <Select.Option value="8_kyu">8 кю</Select.Option>
            <Select.Option value="7_kyu">7 кю</Select.Option>
            <Select.Option value="6_kyu">6 кю</Select.Option>
            <Select.Option value="5_kyu">5 кю</Select.Option>
            <Select.Option value="4_kyu">4 кю</Select.Option>
            <Select.Option value="3_kyu">3 кю</Select.Option>
            <Select.Option value="2_kyu">2 кю</Select.Option>
            <Select.Option value="1_kyu">1 кю</Select.Option>
            <Select.Option value="1_dan">1 дан</Select.Option>
            <Select.Option value="2_dan">2 дан</Select.Option>
            <Select.Option value="3_dan">3 дан</Select.Option>
            <Select.Option value="4_dan">4 дан</Select.Option>
            <Select.Option value="5_dan">5 дан</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="phone"
          label="Телефон"
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="categoryId"
          label="Категория"
          rules={[{ required: true, message: 'Выберите категорию' }]}
          extra={filteredCategories.length === 0 && (form.getFieldValue('dateOfBirth') || form.getFieldValue('weight') || form.getFieldValue('gender')) 
            ? 'Нет подходящих категорий для указанных параметров' 
            : filteredCategories.length < allCategories.length 
              ? `Показано ${filteredCategories.length} из ${allCategories.length} категорий (отфильтровано по возрасту, весу и полу)`
              : undefined}
        >
          <Select
            placeholder="Выберите категорию"
            loading={loadingCategories}
            allowClear
          >
            {filteredCategories.map(category => (
              <Select.Option key={category.id} value={category.id}>
                {category.name} ({category.ageFrom}-{category.ageTo} лет, {category.weightFrom}-{category.weightTo} кг, {category.gender === 'male' ? 'М' : category.gender === 'female' ? 'Ж' : 'М/Ж'})
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="coachId"
          label="Тренер"
          rules={[{ required: false, message: 'Выберите тренера' }]}
        >
          <Select
            placeholder="Выберите тренера"
            loading={loading}
            allowClear
          >
            {coaches.map(coach => (
              <Select.Option key={coach.id} value={coach.id}>
                {coach.fullName || (coach.user ? `${coach.user.lastName || ''} ${coach.user.firstName || ''}${coach.user.middleName ? ` ${coach.user.middleName}` : ''}` : `Тренер ID: ${coach.id}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="status"
          label="Статус"
          rules={[{ required: true, message: 'Выберите статус' }]}
        >
          <Select>
            <Select.Option value="active">Активен</Select.Option>
            <Select.Option value="inactive">Неактивен</Select.Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};